import { useCallback, useMemo, useState } from 'react';

import { nanoid } from 'nanoid';
import omit from 'lodash-es/omit';

import useControlled from '@ecp/common/src/hooks/useControlled';

const useTree = (props) => {
  const {
    data,
    defaultValue,
    onChange,
    onDelete,
    editable,
    onExpand,
    onCheck,
    onSelect,
    onAdd,
    onUpdate,
    treeNodeProps,
    showCheckedCount,
    expandCheckedButtonText,
    addButtonText,
    defaultTitle = '',
    blockAdd,
    disableUnselect = false,
    hideHoverMenu,
    rootless = false,
    customButtonText,
    onCustomButtonClick,
    rootUnchangeable,
    initialSelectedKey,
    ...otherProps
  } = props;
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(initialSelectedKey ? [initialSelectedKey] : []);
  const [createdKey, setCreatedKey] = useState('');
  const [editedKey, setEditedKey] = useState('');

  const isControlledExpandedKeys = useMemo(() => !!otherProps.expandedKeys, [otherProps.expandedKeys]);

  const { value: controlledValue, setValueWhenUncontrolled } = useControlled({
    value: rootless ? [...data] : [{ title: '전체', key: 'ALL', children: [...data] }],
    defaultValue,
  });

  const findTreeNodeWithKey = useCallback((parentKey, data, findKey, callback) => {
    return data.some((item, index, arr) => {
      if (item.key === findKey) callback(item, index, arr, parentKey);
      else if (item.children) {
        return findTreeNodeWithKey(item.key, item.children, findKey, callback);
      }
      return item.key === findKey;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    const changedExpandedKeys = [...expandedKeys];
    const treeSearch = (data) => {
      return data.forEach((item) => {
        if (!changedExpandedKeys.includes(item.key)) changedExpandedKeys.push(item.key);
        if (item.children) treeSearch(item.children);
      });
    };
    treeSearch(controlledValue);
    setExpandedKeys(changedExpandedKeys);
    isControlledExpandedKeys && onExpand(changedExpandedKeys);
  }, [controlledValue, expandedKeys, isControlledExpandedKeys, onExpand]);

  const handleCollapseAll = useCallback(() => {
    setExpandedKeys([]);
    isControlledExpandedKeys && onExpand([]);
  }, [isControlledExpandedKeys, onExpand]);

  const handleExpandChecked = useCallback(() => {
    const changedExpandedKeys = [];
    const treeSearch = (data) => {
      return data.reduce((isExpand, item) => {
        const hasChecked = (item.children && treeSearch(item.children)) || checkedKeys.includes(item.key);
        if (hasChecked) changedExpandedKeys.push(item.key);
        return isExpand || hasChecked;
      }, false);
    };
    treeSearch(controlledValue);
    setExpandedKeys(changedExpandedKeys);
  }, [controlledValue, checkedKeys]);

  const handleAdd = useCallback(() => {
    if (selectedKeys.length === 0) return;

    if (blockAdd && blockAdd()) return;

    const selectedKey = selectedKeys[0];
    const changedData = [...controlledValue];

    const generatedNodeKey = nanoid();
    findTreeNodeWithKey('', changedData, selectedKey, (item) => {
      setCreatedKey(generatedNodeKey);
      setEditedKey(generatedNodeKey);
      item.children = item.children || [];
      item.children.push({ title: defaultTitle, key: generatedNodeKey, parentKey: item.key });
    });
    if (!expandedKeys.includes(selectedKey)) {
      setExpandedKeys((prev) => [...prev, ...selectedKeys]);
    }
    if (checkedKeys.includes(selectedKey)) setCheckedKeys((prev) => [...prev, generatedNodeKey]);
    setValueWhenUncontrolled(changedData);
    onChange && onChange(changedData[0].children);
  }, [
    selectedKeys,
    blockAdd,
    controlledValue,
    findTreeNodeWithKey,
    expandedKeys,
    checkedKeys,
    setValueWhenUncontrolled,
    onChange,
    defaultTitle,
  ]);

  const handleDrop = useCallback(
    (info) => {
      info.event.preventDefault();
      info.event.stopPropagation();
      const dropKey = info.node.key;
      const dragKey = info.dragNode.key;
      const dropPos = info.node.pos.split('-');
      const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

      const changedData = [...controlledValue];

      let dragObj;
      findTreeNodeWithKey('', changedData, dragKey, (item, index, arr) => {
        arr.splice(index, 1);
        dragObj = item;
      });

      let parentKey, childrenLen;
      if (dropPosition === 0) {
        findTreeNodeWithKey('', changedData, dropKey, (item) => {
          item.children = item.children || [];
          parentKey = item.key;
          childrenLen = item.children.length;
          item.children.unshift(dragObj);
        });
      } else {
        findTreeNodeWithKey('', changedData, dropKey, (item, index, arr, pKey) => {
          parentKey = pKey;
          childrenLen = arr.length;
          arr.splice(dropPosition === -1 ? index : index + 1, 0, dragObj);
        });
      }
      const changedCheckedKeys = [...checkedKeys];
      if (!checkedKeys.includes(parentKey) && checkedKeys.includes(dragKey) && childrenLen === 0) {
        changedCheckedKeys.push(parentKey);
      } else if (checkedKeys.includes(parentKey) && !checkedKeys.includes(dragKey)) {
        changedCheckedKeys.push(dragKey);
        const treeSearch = (data) => {
          return data.forEach((item) => {
            if (!changedCheckedKeys.includes(item.key)) changedCheckedKeys.push(item.key);
            if (item.children) treeSearch(item.children);
          });
        };
        treeSearch(dragObj.children);
      }
      setCheckedKeys(changedCheckedKeys);
      setValueWhenUncontrolled(changedData);
      onChange && onChange(changedData[0].children);
    },
    [findTreeNodeWithKey, controlledValue, checkedKeys, onChange, setValueWhenUncontrolled]
  );

  const handleExpand = useCallback(
    (expandedKeys, e) => {
      setExpandedKeys(expandedKeys);
      onExpand && onExpand(expandedKeys, e);
    },
    [onExpand]
  );

  const handleCheck = useCallback(
    (checkedKeys, e) => {
      setCheckedKeys(checkedKeys);
      onCheck && onCheck(checkedKeys, e);
    },
    [onCheck]
  );

  const handleSelect = useCallback(
    (selectedKeys, e) => {
      if (disableUnselect && !e.selected) return;
      setSelectedKeys(selectedKeys);
      onSelect && onSelect(selectedKeys, e);
    },
    [disableUnselect, onSelect]
  );

  const handleSave = useCallback(
    (e, { data: { key } }) => {
      const changedData = [...controlledValue];
      if (e.target.value === '' && createdKey === '') return;
      else if (e.target.value === '') {
        findTreeNodeWithKey('', changedData, key, (item, index, arr) => {
          arr.splice(index, 1);
        });
        setCreatedKey('');
        setExpandedKeys((prev) => prev.filter((k) => key !== k));
        setCheckedKeys((prev) => prev.filter((k) => key !== k));
        setSelectedKeys((prev) => prev.filter((k) => key !== k));
        setValueWhenUncontrolled(changedData);
      } else if (createdKey === '') {
        let updatedItem;
        findTreeNodeWithKey('', changedData, key, (item) => {
          item.title = e.target.value;
          updatedItem = {
            ...omit(item, ['children']),
          };
        });
        setValueWhenUncontrolled(changedData);
        onUpdate && onUpdate(updatedItem);
      } else {
        let addedItem;
        findTreeNodeWithKey('', changedData, key, (item) => {
          item.title = e.target.value;
          addedItem = {
            ...omit(item, ['children']),
          };
        });
        setCreatedKey('');
        setValueWhenUncontrolled(changedData);
        onAdd && onAdd(addedItem);
      }
      onChange && onChange(changedData[0].children);
    },
    [findTreeNodeWithKey, controlledValue, onAdd, onUpdate, onChange, setValueWhenUncontrolled, createdKey]
  );

  const handleDelete = useCallback(
    (e, { data: { key } }) => {
      const deleteCallback = () => {
        const changedData = [...controlledValue];
        findTreeNodeWithKey('', changedData, key, (item, index, arr) => {
          arr.splice(index, 1);
        });
        setExpandedKeys((prev) => prev.filter((k) => key !== k));
        setCheckedKeys((prev) => prev.filter((k) => key !== k));
        setSelectedKeys((prev) => prev.filter((k) => key !== k));
        setValueWhenUncontrolled(changedData);
        onChange && onChange(changedData[0].children);
      };
      onDelete && onDelete(key, deleteCallback);
    },
    [findTreeNodeWithKey, controlledValue, onDelete, onChange, setValueWhenUncontrolled]
  );

  const getRootProps = useCallback(
    () => ({
      showCheckedCount,
      checkedCount: checkedKeys.length,
      expandCheckedButtonText,
      addButtonText,
      onExpandAll: handleExpandAll,
      onCollapseAll: handleCollapseAll,
      onExpandChecked: handleExpandChecked,
      onAdd: handleAdd,
      customButtonText,
      onCustomButtonClick,
    }),
    [
      showCheckedCount,
      checkedKeys,
      expandCheckedButtonText,
      addButtonText,
      handleExpandAll,
      handleCollapseAll,
      handleExpandChecked,
      handleAdd,
      customButtonText,
      onCustomButtonClick,
    ]
  );

  const handleAllowDrop = useCallback(
    ({ dragNode, dropNode, dropPosition }) => {
      return (
        !rootUnchangeable ||
        (dropPosition === 0 && dropNode.key === dragNode.parentKey) || // 최상위 sequence로의 순서 변경
        (dropPosition === 1 && dragNode.parentKey === dropNode.parentKey) // 같은 depth 안에서 순서 변경
      );
    },
    [rootUnchangeable]
  );

  const getTreeProps = useCallback(
    () => ({
      showLine: true,
      treeData: controlledValue,
      expandedKeys,
      checkedKeys,
      selectedKeys,
      autoExpandParent: false,
      onExpand: handleExpand,
      onCheck: handleCheck,
      onSelect: handleSelect,
      onDrop: handleDrop,
      allowDrop: handleAllowDrop,
      ...otherProps,
    }),
    [
      controlledValue,
      expandedKeys,
      checkedKeys,
      selectedKeys,
      handleExpand,
      handleCheck,
      handleSelect,
      handleDrop,
      handleAllowDrop,
      otherProps,
    ]
  );

  const getTreeNodeProps = useCallback(
    () => ({
      ...treeNodeProps,
      editedKey,
      editable,
      hideHoverMenu,
      onSave: handleSave,
      onEdit: setEditedKey,
      onDelete: handleDelete,
    }),
    [treeNodeProps, editedKey, editable, hideHoverMenu, handleSave, handleDelete]
  );

  return {
    getRootProps,
    getTreeProps,
    getTreeNodeProps,
  };
};

export default useTree;
